// Morse code data + dichotomic tree layout
// Binary path: 0 = dash (left child, square), 1 = dot (right child, circle)

export const MORSE_TO_CHAR = {
  ".-": "A", "-...": "B", "-.-.": "C", "-..": "D", ".": "E",
  "..-.": "F", "--.": "G", "....": "H", "..": "I", ".---": "J",
  "-.-": "K", ".-..": "L", "--": "M", "-.": "N", "---": "O",
  ".--.": "P", "--.-": "Q", ".-.": "R", "...": "S", "-": "T",
  "..-": "U", "...-": "V", ".--": "W", "-..-": "X", "-.--": "Y",
  "--..": "Z",
  "-----": "0", ".----": "1", "..---": "2", "...--": "3", "....-": "4",
  ".....": "5", "-....": "6", "--...": "7", "---..": "8", "----.": "9",
};

export const CHAR_TO_MORSE = Object.fromEntries(
  Object.entries(MORSE_TO_CHAR).map(([m, c]) => [c, m])
);

// Build node info from index at a level
// Level 0: root (star). Level L has 2^L slots; index i in binary (L bits) = morse path.
function indexToMorse(level, index) {
  if (level === 0) return "";
  return index
    .toString(2)
    .padStart(level, "0")
    .split("")
    .map((b) => (b === "0" ? "-" : "."))
    .join("");
}

// Layout: compact vertical so the practice panel fits below the tree.
const LEVEL_Y = { 0: 20, 1: 82, 2: 152, 3: 228, 4: 314, 5: 404 };

function nodeAt(level, index, { label = null, show = true } = {}) {
  const morse = indexToMorse(level, index);
  const letter = label !== null ? label : (MORSE_TO_CHAR[morse] || "");
  const shape =
    level === 0 ? "star" : (index % 2 === 0 ? "square" : "circle");
  const x = ((index + 0.5) / Math.pow(2, level)) * 1000;
  const y = LEVEL_Y[level];
  return { id: `L${level}-${index}`, level, index, morse, letter, shape, x, y, show };
}

// Digits at level 5 (morse -> index)
const DIGIT_INDICES = {
  "-----": 0,  // 0
  "----.": 1,  // 9
  "---..": 3,  // 8
  "--...": 7,  // 7
  "-....": 15, // 6
  ".----": 16, // 1
  "..---": 24, // 2
  "...--": 28, // 3
  "....-": 30, // 4
  ".....": 31, // 5
};

// Per-digit x-offsets at level 5 to prevent visual overlap with the bigger
// node size. Right/left direction may flip from the strict dot/dash side
// (acceptable per spec; the symbol shape still encodes the bit).
const DIGIT_SHIFTS = {
  0: 0,    // 0
  1: 31,   // 9 — pushed right of 0
  3: 30,   // 8 — pushed right to clear 9
  7: 0,    // 7
  15: 0,   // 6
  16: 31,  // 1 — pushed right of 6
  24: 0,   // 2
  28: 0,   // 3
  30: 0,   // 4
  31: 31,  // 5 — pushed right of 4
};

// Build the full visible tree (levels 0..4 complete + digits on level 5)
export const TREE_NODES = (() => {
  const nodes = [];
  nodes.push(nodeAt(0, 0, { label: "" }));
  for (let L = 1; L <= 4; L++) {
    for (let i = 0; i < Math.pow(2, L); i++) {
      nodes.push(nodeAt(L, i));
    }
  }
  for (const [m, idx] of Object.entries(DIGIT_INDICES)) {
    const n = nodeAt(5, idx, { label: MORSE_TO_CHAR[m] });
    if (DIGIT_SHIFTS[idx]) n.x += DIGIT_SHIFTS[idx];
    nodes.push(n);
  }
  return nodes;
})();

// Map morse -> node for quick lookup
export const MORSE_TO_NODE = Object.fromEntries(
  TREE_NODES.filter((n) => n.level > 0).map((n) => [n.morse, n])
);

// Edges: parent to child (parent has one fewer bit)
export const TREE_EDGES = (() => {
  const edges = [];
  for (const n of TREE_NODES) {
    if (n.level === 0) continue;
    const parentMorse = n.morse.slice(0, -1);
    const parent =
      parentMorse === ""
        ? TREE_NODES[0]
        : MORSE_TO_NODE[parentMorse];
    if (parent) edges.push({ from: parent, to: n });
  }
  return edges;
})();

// Return chain of node ids from root to the node matching given morse prefix
export function pathForMorse(morse) {
  const ids = ["L0-0"];
  let acc = "";
  for (const ch of morse) {
    acc += ch;
    const n = MORSE_TO_NODE[acc];
    if (n) ids.push(n.id);
  }
  return ids;
}
